import type { Agent, EmailLogEntry, QuoteRecord, Ticket } from '../types';
import { buildPaymentUrl } from './hosts';
import {
  formatEmailLogFailureFooter,
  formatEmailLogSuccessFooter,
  stripEmailLogPreviewBody,
  extractQuoteIdFromPreview,
} from './emailLogDelivery';
import { buildSkyluxQuoteClientMailHtml, buildSkyluxQuoteEmailIntroPlain, buildPaymentEmailSubject, buildQuoteEmailSubject } from './skyluxQuoteMailHtml';
import { normalizePassengers } from './passengers';
import { parseQuoteIdFromTicket } from './quoteTicketLink';
import { sendTicketReceiptEmail } from './sendTicketReceiptEmail';
import { sendTransactionalEmail } from './api';
import { appendEmailLog, loadCms, getQuoteById } from './skyluxStore';

export type ResendFromLogContext = {
  tickets: Ticket[];
  findQuote: (quoteId: string) => QuoteRecord | undefined;
};

export type ResendFromLogResult = { ok: boolean; message: string };

function quoteEmailSubject(quote: QuoteRecord): string {
  return buildQuoteEmailSubject(quote.flight.arrival, quote.pricePerPerson);
}

function findTicketForQuote(tickets: Ticket[], quoteId: string): Ticket | undefined {
  return tickets.find((t) => parseQuoteIdFromTicket(t) === quoteId);
}

async function resendPlainEmail(
  entry: EmailLogEntry,
  agent: Agent,
  html?: string,
): Promise<ResendFromLogResult> {
  const to = entry.to.trim();
  if (!to) return { ok: false, message: 'No recipient on this log entry.' };
  const text = stripEmailLogPreviewBody(entry.preview);
  try {
    const mail = await sendTransactionalEmail({ to, subject: entry.subject, text, html });
    appendEmailLog({
      id: `em_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      at: new Date().toISOString(),
      agentId: agent.id,
      agentEmail: mail.from,
      to,
      subject: entry.subject,
      kind: entry.kind,
      preview: text + formatEmailLogSuccessFooter(mail.from, mail.messageId),
      deliveryStatus: 'sent',
    });
    return { ok: true, message: `Email resent to ${to}.` };
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Unknown error';
    appendEmailLog({
      id: `em_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      at: new Date().toISOString(),
      agentId: agent.id,
      agentEmail: agent.email,
      to,
      subject: entry.subject,
      kind: entry.kind,
      preview: text + formatEmailLogFailureFooter(msg),
      deliveryStatus: 'failed',
      errorMessage: msg,
    });
    return { ok: false, message: msg };
  }
}

async function resendQuoteEmail(quote: QuoteRecord, agent: Agent): Promise<ResendFromLogResult> {
  const to = quote.clientSnapshot.email?.trim();
  if (!to) return { ok: false, message: 'Quote has no client email.' };

  const passengerCount = Math.max(1, quote.adults ?? normalizePassengers(quote).length);
  const subject = quoteEmailSubject(quote);
  const totalUsd = quote.pricePerPerson * passengerCount;
  const quoteMailAgent = {
    name: agent.name,
    nickname: agent.nickname,
    email: agent.email,
    avatar: agent.avatar,
  };
  const preview = [
    buildSkyluxQuoteEmailIntroPlain({
      clientName: quote.clientSnapshot.name,
      agent: quoteMailAgent,
      flight: quote.flight,
      primaryCta: 'booking',
    }),
    '',
    `Total: ${totalUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`,
    `Review & book (enter passenger details): ${quote.bookingUrl}`,
  ].join('\n');

  const html = buildSkyluxQuoteClientMailHtml({
    clientName: quote.clientSnapshot.name,
    introText: '',
    flight: quote.flight,
    pricePerPerson: quote.pricePerPerson,
    passengerCount,
    bookingUrl: quote.bookingUrl,
    paymentUrl: buildPaymentUrl(quote.id),
    quoteId: quote.id,
    agent: quoteMailAgent,
    primaryCta: 'booking',
  });

  try {
    const mail = await sendTransactionalEmail({ to, subject, text: preview, html });
    appendEmailLog({
      id: `em_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      at: new Date().toISOString(),
      agentId: agent.id,
      agentEmail: mail.from,
      to,
      subject,
      kind: 'quote',
      preview: `${preview}\n\n— Quote email (resend): Review & book${formatEmailLogSuccessFooter(mail.from, mail.messageId)}`,
      deliveryStatus: 'sent',
      quoteId: quote.id,
    });
    return { ok: true, message: `Quote email resent to ${to}.` };
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Unknown error';
    appendEmailLog({
      id: `em_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      at: new Date().toISOString(),
      agentId: agent.id,
      agentEmail: agent.email,
      to,
      subject,
      kind: 'quote',
      preview: preview + formatEmailLogFailureFooter(msg),
      deliveryStatus: 'failed',
      errorMessage: msg,
      quoteId: quote.id,
    });
    return { ok: false, message: msg };
  }
}

async function resendOafEmail(quote: QuoteRecord, agent: Agent): Promise<ResendFromLogResult> {
  const to = quote.clientSnapshot.email?.trim();
  if (!to) return { ok: false, message: 'Quote has no client email.' };

  const travelers = normalizePassengers(quote);
  if (travelers.length === 0) {
    return { ok: false, message: 'Quote has no passengers on file. Send OAF from the quote modal after entering travelers.' };
  }

  const passengerCount = travelers.length;
  const paymentUrl = buildPaymentUrl(quote.id);
  const subject = buildPaymentEmailSubject(quote.flight.arrival, quote.pricePerPerson);
  const totalUsd = quote.pricePerPerson * passengerCount;
  const quoteMailAgent = {
    name: agent.name,
    nickname: agent.nickname,
    email: agent.email,
    avatar: agent.avatar,
  };
  const preview = [
    buildSkyluxQuoteEmailIntroPlain({
      clientName: quote.clientSnapshot.name,
      agent: quoteMailAgent,
      flight: quote.flight,
      primaryCta: 'payment',
    }),
    '',
    ...travelers.map((row, i) => `${i + 1}. ${row.name}${row.age != null ? ` (age ${row.age})` : ''}`),
    '',
    `Total: ${totalUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`,
    `Complete payment (OAF checkout): ${paymentUrl}`,
  ].join('\n');

  const html = buildSkyluxQuoteClientMailHtml({
    clientName: quote.clientSnapshot.name,
    introText: '',
    flight: quote.flight,
    pricePerPerson: quote.pricePerPerson,
    passengerCount,
    bookingUrl: quote.bookingUrl,
    paymentUrl,
    quoteId: quote.id,
    agent: quoteMailAgent,
    passengers: travelers,
    primaryCta: 'payment',
  });

  try {
    const mail = await sendTransactionalEmail({ to, subject, text: preview, html });
    appendEmailLog({
      id: `em_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      at: new Date().toISOString(),
      agentId: agent.id,
      agentEmail: mail.from,
      to,
      subject,
      kind: 'oaf',
      preview: `${preview}\n\n— OAF email (resend): Complete payment${formatEmailLogSuccessFooter(mail.from, mail.messageId)}`,
      deliveryStatus: 'sent',
      quoteId: quote.id,
    });
    return { ok: true, message: `OAF payment email resent to ${to}.` };
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Unknown error';
    appendEmailLog({
      id: `em_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      at: new Date().toISOString(),
      agentId: agent.id,
      agentEmail: agent.email,
      to,
      subject,
      kind: 'oaf',
      preview: preview + formatEmailLogFailureFooter(msg),
      deliveryStatus: 'failed',
      errorMessage: msg,
      quoteId: quote.id,
    });
    return { ok: false, message: msg };
  }
}

export async function resendFromEmailLog(
  entry: EmailLogEntry,
  agent: Agent,
  ctx: ResendFromLogContext,
): Promise<ResendFromLogResult> {
  const quoteId = entry.quoteId ?? extractQuoteIdFromPreview(entry.preview);

  if (entry.kind === 'ticket_receipt') {
    if (!quoteId) return { ok: false, message: 'Cannot resend — quote reference missing from log.' };
    const quote = ctx.findQuote(quoteId) ?? getQuoteById(quoteId);
    if (!quote) return { ok: false, message: 'Quote not found. Refresh and try again.' };
    const ticket = findTicketForQuote(ctx.tickets, quoteId);
    if (!ticket) return { ok: false, message: 'Support ticket not found for this quote.' };
    const result = await sendTicketReceiptEmail(quote, ticket, agent, { resend: true });
    return { ok: result.ok, message: result.message };
  }

  if (entry.kind === 'quote') {
    if (!quoteId) return { ok: false, message: 'Cannot resend — quote reference missing from log.' };
    const quote = ctx.findQuote(quoteId) ?? getQuoteById(quoteId);
    if (!quote) return { ok: false, message: 'Quote not found. Refresh and try again.' };
    return resendQuoteEmail(quote, agent);
  }

  if (entry.kind === 'oaf') {
    if (!quoteId) return { ok: false, message: 'Cannot resend — quote reference missing from log.' };
    const quote = ctx.findQuote(quoteId) ?? getQuoteById(quoteId);
    if (!quote) return { ok: false, message: 'Quote not found. Refresh and try again.' };
    return resendOafEmail(quote, agent);
  }

  if (entry.kind === 'payment_reminder') {
    if (quoteId) {
      const quote = ctx.findQuote(quoteId) ?? getQuoteById(quoteId);
      if (quote) {
        const cms = loadCms();
        const preview = `${cms.paymentReminderBody}\n\nPay: ${quote.paymentUrl}\nAmount: $${quote.pricePerPerson}`;
        const subject = entry.subject || `Reminder: payment — ${quote.flight.departure} to ${quote.flight.arrival}`;
        return resendPlainEmail({ ...entry, subject, preview, kind: 'payment_reminder' }, agent);
      }
    }
    return resendPlainEmail(entry, agent);
  }

  return resendPlainEmail(entry, agent);
}
